import { SaucerClient } from "@saucer/core";
import type {
  AdminAccountPage,
  AdminAccountUpdate,
  ApiToken,
  ApiTokenSecret,
  Column,
  DataSource,
  DatabaseListResponse,
  DbCreateRequest,
  DbCredentials,
  Pipe,
  PipeNode,
  PlatformStatsResponse,
  TokenScope,
  SaucerFile,
  SearchHit,
  StatsResponse,
  Usage,
  User,
} from "@saucer/core";

/** Same-origin: the Worker serves this bundle and the API from one host. */
export const api = new SaucerClient(window.location.origin);

export const logout = () => {
  forgetCachedSession();
  forgetCachedFiles();
  return fetch("/api/auth/logout", { method: "POST" });
};

/**
 * The session probe, cached stale-while-revalidate: the last known answer is
 * kept in sessionStorage so a repeat visit paints instantly, and the network
 * round trip only ever runs in the background to correct it.
 */
const SESSION_CACHE_KEY = "saucer:session";

export interface SessionInfo {
  user: User;
}

export function cachedSession(): SessionInfo | null {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const body = JSON.parse(raw) as SessionInfo;
    return body.user ? body : null;
  } catch {
    return null;
  }
}

export function forgetCachedSession(): void {
  try {
    sessionStorage.removeItem(SESSION_CACHE_KEY);
  } catch {
    // Storage may be unavailable; the network path still works.
  }
}

export async function currentSession(): Promise<SessionInfo | null> {
  const response = await fetch("/api/auth/session", { credentials: "include" });
  if (!response.ok) return null;
  const body = (await response.json()) as SessionInfo | { user: null };
  try {
    if (body.user) sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(body));
    else sessionStorage.removeItem(SESSION_CACHE_KEY);
  } catch {
    // Storage may be unavailable; the network path still works.
  }
  return body.user ? (body as SessionInfo) : null;
}

/**
 * The dashboard's first page, cached the same way as the session probe: the
 * last answer paints immediately on a repeat visit and the request that
 * corrects it runs behind the painted list rather than in front of it.
 */
const FILES_CACHE_KEY = "saucer:files";

export interface CachedFiles {
  files: SaucerFile[];
  usage: Usage | null;
}

export function cachedFiles(): CachedFiles | null {
  try {
    const raw = sessionStorage.getItem(FILES_CACHE_KEY);
    if (!raw) return null;
    const body = JSON.parse(raw) as CachedFiles;
    return Array.isArray(body.files) ? body : null;
  } catch {
    return null;
  }
}

export function rememberFiles(body: CachedFiles): void {
  try {
    sessionStorage.setItem(FILES_CACHE_KEY, JSON.stringify(body));
  } catch {
    // Storage may be unavailable, or full; the network path still works.
  }
}

export function forgetCachedFiles(): void {
  try {
    sessionStorage.removeItem(FILES_CACHE_KEY);
  } catch {
    // Storage may be unavailable; the network path still works.
  }
}

export const signInUrl = (invite: string, next = "/dashboard") => {
  const url = new URL("/api/auth/github/start", window.location.origin);
  if (invite) url.searchParams.set("invite", invite.trim());
  url.searchParams.set("next", next);
  return url.toString();
};

export interface TokenSummary {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export async function listTokens(): Promise<TokenSummary[]> {
  const response = await fetch("/api/tokens");
  const body = (await response.json()) as { tokens: TokenSummary[] };
  return body.tokens;
}

export async function createToken(name: string): Promise<{ id: string; secret: string }> {
  const response = await fetch("/api/tokens", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw new Error("Could not create that token.");
  return response.json() as Promise<{ id: string; secret: string }>;
}

export const revokeToken = (id: string) => fetch(`/api/tokens/${id}`, { method: "DELETE" });

export const decideCliLogin = (userCode: string, approve: boolean) =>
  fetch("/api/auth/cli/decide", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ userCode, approve }),
  });

export const cliLoginPending = (userCode: string) =>
  fetch(`/api/auth/cli/pending?code=${encodeURIComponent(userCode)}`);

export async function search(query: string): Promise<SearchHit[]> {
  const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`);
  if (!response.ok) return [];
  const body = (await response.json()) as { hits: SearchHit[] };
  return body.hits;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  at: number;
}

export async function assistantHistory(): Promise<ChatMessage[]> {
  const response = await fetch("/api/assistant");
  if (!response.ok) return [];
  const body = (await response.json()) as { messages: ChatMessage[] };
  return body.messages ?? [];
}

export async function askAssistant(message: string): Promise<ChatMessage[]> {
  const response = await fetch("/api/assistant", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ message }),
  });
  if (!response.ok) throw new Error("The assistant could not answer that.");
  const body = (await response.json()) as { messages: ChatMessage[] };
  return body.messages ?? [];
}

export const clearAssistant = () => fetch("/api/assistant", { method: "DELETE" });

export async function loadStats(): Promise<StatsResponse> {
  const response = await fetch("/api/stats");
  if (!response.ok) throw new Error("Stats are unavailable right now.");
  return response.json() as Promise<StatsResponse>;
}

export async function snapUrl(url: string): Promise<SaucerFile> {
  const response = await fetch("/api/snap", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ url }),
  });
  if (!response.ok) throw new Error("That page could not be captured.");
  return response.json() as Promise<SaucerFile>;
}

// ---- managed databases -----------------------------------------------------

export async function listDatabases(): Promise<DatabaseListResponse> {
  const response = await fetch("/api/databases");
  if (!response.ok) throw new Error("Could not load your databases.");
  return response.json() as Promise<DatabaseListResponse>;
}

/**
 * The password in the reply exists only here. It is not stored anywhere it can
 * be read back, so the caller has to put it in front of someone before this
 * promise's value goes out of scope.
 */
export async function createDatabase(input: DbCreateRequest): Promise<DbCredentials> {
  const response = await fetch("/api/databases", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? "That database could not be created.");
  }
  return response.json() as Promise<DbCredentials>;
}

export async function rotateDatabasePassword(id: string): Promise<DbCredentials> {
  const response = await fetch(`/api/databases/${id}/rotate`, { method: "POST" });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? "Could not rotate that password.");
  }
  return response.json() as Promise<DbCredentials>;
}

export async function deleteDatabase(id: string): Promise<void> {
  const response = await fetch(`/api/databases/${id}`, { method: "DELETE" });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? "Could not delete that database.");
  }
}

// ---- the analytics product -------------------------------------------------

/** Every route here is workspace-scoped and the Worker checks ownership. */
const ws = (id: string, path: string) => `/api/workspaces/${id}${path}`;

async function send<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init?.headers } : init?.headers,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? "That did not work.");
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const listDataSources = (id: string) =>
  send<{ datasources: DataSource[]; ingestHost: string }>(ws(id, "/datasources"));

export const createDataSource = (
  id: string,
  input: { name: string; columns: Column[]; sortingKey: string[]; partitionBy: string; ttl: string },
) => send<DataSource>(ws(id, "/datasources"), { method: "POST", body: JSON.stringify(input) });

export const deleteDataSource = (id: string, name: string) =>
  send<void>(ws(id, `/datasources/${name}`), { method: "DELETE" });

export const listPipes = (id: string) =>
  send<{ pipes: Pipe[]; apiHost: string }>(ws(id, "/pipes"));

export const savePipe = (
  id: string,
  name: string,
  input: {
    description: string;
    nodes: PipeNode[];
    endpointNode: string | null;
    cacheSeconds: number;
    maxThreads: number;
  },
) => send<Pipe>(ws(id, `/pipes/${name}`), { method: "PUT", body: JSON.stringify(input) });

export const deletePipe = (id: string, name: string) =>
  send<void>(ws(id, `/pipes/${name}`), { method: "DELETE" });

/** Compiles without saving, so the editor can show what will actually run. */
export const explainPipe = (id: string, name: string, nodes: PipeNode[], endpointNode: string) =>
  send<{ sql: string; params: { name: string; type: string }[] }>(
    ws(id, `/pipes/${name}/explain`),
    { method: "POST", body: JSON.stringify({ nodes, endpointNode }) },
  );

export const listWorkspaceTokens = async (id: string) =>
  (await send<{ tokens: ApiToken[] }>(ws(id, "/tokens"))).tokens;

export const createApiToken = (id: string, name: string, scopes: TokenScope[]) =>
  send<ApiTokenSecret>(ws(id, "/tokens"), {
    method: "POST",
    body: JSON.stringify({ name, scopes }),
  });

export const deleteApiToken = (id: string, tokenId: string) =>
  send<void>(ws(id, `/tokens/${tokenId}`), { method: "DELETE" });

/** One row per pipe per reporting window, as the daemon measured it. */
export interface EndpointWindow {
  id: string;
  pipe: string;
  requests: number;
  errors: number;
  p50: number;
  p95: number;
  p99: number;
  rowsRead: number;
  bytesRead: number;
  recordedAt: string;
}

export const listWorkspaceStats = async (id: string) =>
  (await send<{ windows: EndpointWindow[] }>(ws(id, "/stats"))).windows;

// ---- administration --------------------------------------------------------

export async function loadAccounts(): Promise<AdminAccountPage> {
  const response = await fetch("/api/admin/accounts");
  if (!response.ok) throw new Error("Could not load accounts.");
  return response.json() as Promise<AdminAccountPage>;
}

export async function updateAccount(id: string, patch: AdminAccountUpdate): Promise<void> {
  const response = await fetch(`/api/admin/accounts/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? "That change did not stick.");
  }
}

export async function loadPlatformStats(): Promise<PlatformStatsResponse> {
  const response = await fetch("/api/admin/stats");
  if (!response.ok) throw new Error("Could not load platform stats.");
  return response.json() as Promise<PlatformStatsResponse>;
}
